import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { SlidersHorizontal } from 'lucide-react';
interface SlideCalculatorProps {
  onContinue: () => void;
}
export function SlideCalculator({
  onContinue
}: SlideCalculatorProps) {
  const [revenueB, setRevenueB] = useState<number>(5);
  const [legacyPercent, setLegacyPercent] = useState<number>(0);
  const [improvementPercent, setImprovementPercent] = useState<number>(0);
  const [animationDone, setAnimationDone] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  useEffect(() => {
    const t0 = setTimeout(() => setRevenueB(5), 600);
    const t1 = setTimeout(() => setLegacyPercent(35), 1400);
    const t2 = setTimeout(() => setImprovementPercent(1), 2200);
    const t3 = setTimeout(() => setAnimationDone(true), 3200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  const calculateUpside = () => revenueB * 1000 * (legacyPercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 800
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    upsideValue.set(calculateUpside());
  }, [revenueB, legacyPercent, improvementPercent]);
  const handleUserChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInteracted(true);
    setter(Number(e.target.value));
  };
  return <div className="h-full w-full flex flex-col px-12 py-12 md:px-24 md:py-16 max-w-7xl mx-auto" data-id="element-188" data-mp="qjf83lj3psmp">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-white mb-8" data-id="element-189" data-mp="llwvnl5vkv9y">
        Even a <span className="text-teal-400" data-id="element-190" data-mp="cmteimq9qpy3">{improvementPercent}%</span>{' '}
        improvement across the legacy portfolio =
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-191" data-mp="6zhp9fryt5dh">
        <div className="lg:col-span-5 space-y-7 bg-navy-900/70 p-8 rounded-3xl border border-navy-600 relative" data-id="element-192" data-mp="3p4te9cch1myl">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone && !userInteracted ? 1 : 0
        }} className="absolute -top-12 left-0 right-0 flex items-center justify-center gap-2" data-id="element-193" data-mp="3u3xkbu43n4kp">
            <motion.div animate={{
            y: [0, -4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5
          }} className="flex items-center gap-2 bg-teal-400/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium border border-teal-400/30" data-id="element-194" data-mp="bwgf3m4liq5v">
              <SlidersHorizontal className="w-4 h-4" data-id="element-195" data-mp="1ir79c0zkusw5" />
              Adjust the sliders — set your own numbers
            </motion.div>
          </motion.div>

          <div className="space-y-3" data-id="element-196" data-mp="1epvixt8vq3mw">
            <div className="flex justify-between items-end" data-id="element-197" data-mp="1sh8vgn8de18f">
              <label className="text-white/80 font-medium" data-id="element-198" data-mp="1sjmqiw31vdfz">
                Pharma Division Revenue
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-199" data-mp="zsv06f8o1j0k">
                €{revenueB}B
              </span>
            </div>
            <input type="range" min="1" max="60" step="1" value={revenueB} onChange={handleUserChange(setRevenueB)} className="w-full" data-id="element-200" data-mp="vs1d1o31s88q" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-201" data-mp="14u0v8jpstid9">
              <span data-id="element-202" data-mp="2l4mmy5tau4l5">€1B</span>
              <span data-id="element-203" data-mp="2l80qvihm2lup">€60B</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-204" data-mp="2g6bdvxatvy3z">
            <div className="flex justify-between items-end" data-id="element-205" data-mp="2g9h0n5vzinwj">
              <label className="text-white/80 font-medium" data-id="element-206" data-mp="praot0y9750i">
                % from Legacy / Off-Patent Brands
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-207" data-mp="uqx4ri5guzd0">
                €{(revenueB * legacyPercent / 100).toFixed(1)}B
              </span>
            </div>
            <input type="range" min="10" max="80" step="5" value={legacyPercent} onChange={handleUserChange(setLegacyPercent)} className="w-full" data-id="element-208" data-mp="13rbseo5wkqjo" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-209" data-mp="1ie4xz1pmehyo">
              <span data-id="element-210" data-mp="1ih0xguh49ep8">10%</span>
              <span className="text-teal-400/60" data-id="element-211" data-mp="1ne9fmv29liqh">{legacyPercent}%</span>
              <span data-id="element-212" data-mp="1nfknb5ytzamv">80%</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-213" data-mp="1rgxqf8ajgzq3">
            <div className="flex justify-between items-end" data-id="element-214" data-mp="1wefqe96kw4ly">
              <label className="text-white/80 font-medium" data-id="element-215" data-mp="1wez6rlgkykxv">
                Target Improvement
              </label>
              <span className="text-2xl font-serif text-teal-400" data-id="element-216" data-mp="3gudupxhm40et">
                {improvementPercent}%
              </span>
            </div>
            <input type="range" min="1" max="20" step="1" value={improvementPercent} onChange={handleUserChange(setImprovementPercent)} className="w-full" data-id="element-217" data-mp="37vacevq9rp1d" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-218" data-mp="3jerihm2168m">
              <span data-id="element-219" data-mp="3vm68w65o78ey">1%</span>
              <span data-id="element-220" data-mp="3quo7jgzqj5tg">20%</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col items-center lg:items-start justify-center text-center lg:text-left" data-id="element-221" data-mp="3qstibqhfkrjk">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className="space-y-4" data-id="element-222" data-mp="n5kuuay6lp4z">
            <h3 className="text-2xl text-white/60 font-medium uppercase tracking-widest" data-id="element-223" data-mp="169wqjmg5vpf5">
              Unlocked Value
            </h3>
            <div className="flex items-baseline gap-2" data-id="element-224" data-mp="si03vxx9i7q5">
              <span className="text-6xl md:text-8xl lg:text-9xl font-serif text-teal-400 tracking-tight" data-id="element-225" data-mp="wk4sujcrmg6g">
                €
              </span>
              <motion.span className="text-6xl md:text-8xl lg:text-9xl font-serif text-teal-400 tracking-tight" data-id="element-226" data-mp="ijtyh6c8yjgj">
                {displayUpside}
              </motion.span>
              <span className="text-4xl md:text-6xl font-serif text-teal-400" data-id="element-227" data-mp="9kp4mdbmreap">
                M
              </span>
            </div>
            <p className="text-xl text-white/70 mt-6 border-l-2 border-teal-400/40 pl-4 py-2" data-id="element-228" data-mp="dm0tuhqldxid">
              This value is currently unmanaged and under-optimized.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone ? 1 : 0
        }} transition={{
          delay: 0.5
        }} className="mt-12" data-id="element-229" data-mp="27yqqh78chktg"></motion.div>
        </div>
      </div>
    </div>;
}